import { NgFor } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatLabel } from '@angular/material/form-field';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';

import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { ObservationSettingEnum, ObservationSettings } from '../../models/cycle.model';

export interface ObservationSettingsDialogInputData extends AbstractFormDialogData {
  settings: ObservationSettings;
}

export interface ObservationSettingsDialogResultData {
  settings: ObservationSettings;
}

@Component({
  selector: 'app-observation-settings',
  templateUrl: './observation-settings.component.html',
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    NgFor,
    MatLabel,
    MatSlideToggle,
    TranslatePipe,
  ],
})
export class ObservationSettingsComponent extends AbstractFormDialog<ObservationSettingsDialogResultData> {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ObservationSettingsDialogInputData,
    protected readonly mdDialogRef: MatDialogRef<
      ObservationSettingsComponent,
      AbstractDialogResultData<ObservationSettingsDialogResultData>
    >,
    private readonly locale: LocaleService,
  ) {
    super();
    this.data.title = this.locale.translate('observation-settings.title');

    if (this.data.settings) {
      this.formGroup.controls.animalWeightInGrams.setValue(
        this.data.settings.animalWeightInGrams === ObservationSettingEnum.Daily,
      );
      this.formGroup.controls.coefficientOfVariation.setValue(
        this.data.settings.coefficientOfVariation === ObservationSettingEnum.Daily,
      );
      this.formGroup.controls.feedIntakeInGramsPerAnimal.setValue(
        this.data.settings.feedIntakeInGramsPerAnimal === ObservationSettingEnum.Daily,
      );
      this.formGroup.controls.feedName.setValue(
        this.data.settings.feedName === ObservationSettingEnum.Daily,
      );
      this.formGroup.controls.lightIntensityInLux.setValue(
        this.data.settings.lightIntensityInLux === ObservationSettingEnum.Daily,
      );
      this.formGroup.controls.mortality.setValue(
        this.data.settings.mortality === ObservationSettingEnum.Daily,
      );
      this.formGroup.controls.notes.setValue(
        this.data.settings.notes === ObservationSettingEnum.Daily,
      );
      this.formGroup.controls.numberOfMinutesLight.setValue(
        this.data.settings.numberOfMinutesLight === ObservationSettingEnum.Daily,
      );
      this.formGroup.controls.uniformity.setValue(
        this.data.settings.uniformity === ObservationSettingEnum.Daily,
      );
      this.formGroup.controls.waterIntakeInLitersTotal.setValue(
        this.data.settings.waterIntakeInLitersTotal === ObservationSettingEnum.Daily,
      );
    } else {
      this.formGroup.controls.animalWeightInGrams.setValue(true);
      this.formGroup.controls.coefficientOfVariation.setValue(true);
      this.formGroup.controls.feedIntakeInGramsPerAnimal.setValue(true);
      this.formGroup.controls.feedName.setValue(true);
      this.formGroup.controls.lightIntensityInLux.setValue(true);
      this.formGroup.controls.mortality.setValue(true);
      this.formGroup.controls.notes.setValue(true);
      this.formGroup.controls.numberOfMinutesLight.setValue(true);
      this.formGroup.controls.uniformity.setValue(true);
      this.formGroup.controls.waterIntakeInLitersTotal.setValue(true);
    }
  }

  controlNames: string[] = [
    'animalWeightInGrams',
    'uniformity',
    'coefficientOfVariation',
    'feedName',
    'feedIntakeInGramsPerAnimal',
    'mortality',
    'waterIntakeInLitersTotal',
    'numberOfMinutesLight',
    'lightIntensityInLux',
    'notes',
  ];

  formGroup = new FormGroup({
    animalWeightInGrams: new FormControl(),
    uniformity: new FormControl(),
    coefficientOfVariation: new FormControl(),
    feedName: new FormControl(),
    feedIntakeInGramsPerAnimal: new FormControl(),
    mortality: new FormControl(),
    waterIntakeInLitersTotal: new FormControl(),
    numberOfMinutesLight: new FormControl(),
    lightIntensityInLux: new FormControl(),
    notes: new FormControl(),
  });

  save() {
    this.confirm({
      settings: {
        animalWeightInGrams: this.formGroup.controls.animalWeightInGrams.value
          ? ObservationSettingEnum.Daily
          : ObservationSettingEnum.Off,
        uniformity: this.formGroup.controls.uniformity.value
          ? ObservationSettingEnum.Daily
          : ObservationSettingEnum.Off,
        coefficientOfVariation: this.formGroup.controls.coefficientOfVariation.value
          ? ObservationSettingEnum.Daily
          : ObservationSettingEnum.Off,
        feedName: this.formGroup.controls.feedName.value
          ? ObservationSettingEnum.Daily
          : ObservationSettingEnum.Off,
        feedIntakeInGramsPerAnimal: this.formGroup.controls.feedIntakeInGramsPerAnimal.value
          ? ObservationSettingEnum.Daily
          : ObservationSettingEnum.Off,
        mortality: this.formGroup.controls.mortality.value
          ? ObservationSettingEnum.Daily
          : ObservationSettingEnum.Off,
        waterIntakeInLitersTotal: this.formGroup.controls.waterIntakeInLitersTotal.value
          ? ObservationSettingEnum.Daily
          : ObservationSettingEnum.Off,
        numberOfMinutesLight: this.formGroup.controls.numberOfMinutesLight.value
          ? ObservationSettingEnum.Daily
          : ObservationSettingEnum.Off,
        lightIntensityInLux: this.formGroup.controls.lightIntensityInLux.value
          ? ObservationSettingEnum.Daily
          : ObservationSettingEnum.Off,
        notes: this.formGroup.controls.notes.value
          ? ObservationSettingEnum.Daily
          : ObservationSettingEnum.Off,
      },
    });
  }
}
